import _reduce from 'lodash/reduce';
import _merge from 'lodash/merge';
import _last from 'lodash/last';
import _isString from 'lodash/isString';
import _isObject from 'lodash/isObject';
import _has from 'lodash/has';
import _defaults from 'lodash/defaults';
import { css } from 'emotion';
import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';

var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];

    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }

  return target;
};













var objectWithoutProperties = function (obj, keys) {
  var target = {};

  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }

  return target;
};







var slicedToArray = function () {
  function sliceIterator(arr, i) {
    var _arr = [];
    var _n = true;
    var _d = false;
    var _e = undefined;

    try {
      for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
        _arr.push(_s.value);

        if (i && _arr.length === i) break;
      }
    } catch (err) {
      _d = true;
      _e = err;
    } finally {
      try {
        if (!_n && _i["return"]) _i["return"]();
      } finally {
        if (_d) throw _e;
      }
    }

    return _arr;
  }

  return function (arr, i) {
    if (Array.isArray(arr)) {
      return arr;
    } else if (Symbol.iterator in Object(arr)) {
      return sliceIterator(arr, i);
    } else {
      throw new TypeError("Invalid attempt to destructure non-iterable instance");
    }
  };
}();













var toConsumableArray = function (arr) {
  if (Array.isArray(arr)) {
    for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) arr2[i] = arr[i];

    return arr2;
  } else {
    return Array.from(arr);
  }
};

var appendHTTPS = true;
var mediaQueryConfig = {
  dpr2: '(min-resolution: 144dpi)', // 1.5x devices and up get 2x images
  dpr3: '(min-resolution: 240dpi)', // 2.5x devices and up get 3x images
  portrait: '(orientation: portrait)',
  landscape: '(orientation: landscape)'
};
var variantConfig = {
  default: {
    quality: 85,
    density: 1
  },
  dpr2: {
    quality: 40,
    density: 2
  },
  dpr3: {
    quality: 30,
    density: 3
  }
};

function canUseDOM() {
  return !!(typeof window !== 'undefined' && window.document && window.document.createElement);
}

function canUseWebP() {
  var elem = document.createElement('canvas');

  if (elem.getContext && elem.getContext('2d')) {
    // was able or not to get WebP representation
    return elem.toDataURL('image/webp').indexOf('data:image/webp') == 0;
  } else {
    // very old browser like IE 8, canvas not supported
    return false;
  }
}

/**
 * Sets the default options.
 *
 * @param {Object} m Media query config. Object consists of strings representing
 *  the media queries to be used. E.g.:
 *  {
 *    xs: `(min-width: 360px)`,
 *    sm: `(min-width: 600px)`,
 *  }
 * @param {Object} v Variants config. Determines the default settings to be used for
 *  variant sizes of images. E.g.:
 *  {
 *    default: {
 *      quality: 85,
 *      density: 1,
 *    },
 *    dpr2: {
 *      quality: 35,
 *      density: 2,
 *    },
 *  }
 */
function setup() {
  var m = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : mediaQueryConfig;
  var v = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : variantConfig;
  var opts = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};

  var options = _defaults({}, opts, {
    appendHTTPS: true
  });

  options.appendHTTPS = appendHTTPS;

  mediaQueryConfig = _extends({}, mediaQueryConfig, m);
  variantConfig = _extends({}, variantConfig, v);
}

/**
 * Multiplies a pixel value at density 1, by a given pixel density.
 * E.g. a value of 400 and a density of 2 would return 800.
 *
 * @param {float} density Pixel density scale factor
 * @param {int} value Pixel value to scale at density 1
 * @returns {int} Scaled pixel value to given density
 */
function getRealDimension(density, value) {
  return density * value;
}

/**
 * Gets the format of an image from its file extension
 *
 * @param {string} imageUrl Image url
 * @returns {string} Image format
 */
function getImageFormat(imageUrl) {
  var fm = _last(imageUrl.split('.')).toLowerCase();
  return fm === 'jpeg' ? 'jpg' : fm;
}

/**
 * Builds a contentful image url for resizing an image based on the
 * supplied params. Check the contentful image api for details of all
 * possible parameters.
 *
 * NOTE: This method adds 'https:' to the beginning of supplied urls as
 * Contentful images are supplied with a link without a url scheme.
 *
 * @param {string} imageUrl Contentful default image url
 * @param {Object} param1 Parameters for manipulating the image
 * @returns {string} Contentful resized image url
 */
function getScaledImageUrl(imageUrl, userParams) {
  var formats = ['jpg', 'png', 'webp'];
  var originalFormat = getImageFormat(imageUrl);

  if (formats.indexOf(originalFormat) === -1 && userParams.fm !== 'webp') {
    return appendHTTPS ? 'https:' + imageUrl : imageUrl;
  }

  var params = _defaults({}, userParams, {
    fm: originalFormat,
    q: 85
  });

  if (params.fm === 'jpg') params.fl = 'progressive';
  if (params.w && params.h && params.fit == null) params.fit = 'fill';

  var url = imageUrl + '?' + queryString.stringify(params);

  return appendHTTPS ? 'https:' + url : url;
}

/**
 * Gets the media query string for a media query alias name.
 *
 * @param {string} size Media Query name
 * @returns {string} Media query string
 */
function getMediaQueryForAlias(mediaQuery) {
  if (!mediaQuery) return null;

  var mediaQueriesAliases = _isString(mediaQuery) ? [mediaQuery] : mediaQuery;

  if (!mediaQueriesAliases.length) return null;

  var mqRegex = /(\([\w\W]+?:\s?[\w\d]+\))/g;

  return mediaQueriesAliases.reduce(function (acc, alias) {
    var mqString = mediaQueryConfig[alias];
    var queries = [];

    if (!mqString) {
      var matches = void 0;
      while ((matches = mqRegex.exec(alias)) != null) {
        queries.push(matches[0]);
      }
    } else {
      queries = [mqString];
    }

    if (!queries.length) return acc;

    if (acc === '') return queries.join(' and ');
    return acc + ' and ' + queries.join(' and ');
  }, '');
}

/**
 * Gets the mediaquery string for a mediaquery alias name and variant.
 *
 * @param {string} size Screen size mediaquery identifier
 * @param {string} variant Mediaquery variant e.g. resolution
 * @returns {string} Mediaquery to be inserted into @media
 */
function getMediaQueryForAliasVariantCombo(mediaQuery, variant) {
  var queries = _isString(mediaQuery) ? [mediaQuery] : mediaQuery;
  var variantString = variant === 'default' ? 'default' : getMediaQueryForAlias(variant);

  if (!variantString) {
    throw new Error('No media query was found for variant alias provided. Received: ' + JSON.stringify(variant));
  }

  if (queries.length === 1 && queries[0] === 'default') {
    if (variant === 'default') {
      return null;
    } else {
      return variantString;
    }
  } else {
    var mqString = getMediaQueryForAlias(mediaQuery);

    if (!mqString) {
      throw new Error('No media query was found for media query alias provided. Received: ' + JSON.stringify(mediaQuery));
    }

    if (variant === 'default') {
      return mqString;
    } else {
      return mqString + ' and ' + variantString;
    }
  }
}

/**
 * Builds an object of image urls for specified image sizes and variants.
 * The input array takes the form of:
 * [
 *  {
 *    mediaQuery: 'default',
 *    params: { w: 600, h: 470, fit: 'fill' },
 *    variants: ['dpr2', 'dpr3'],
 *  },
 *  {
 *    mediaQuery: 'sm',
 *    params: { w: 800, h: 600 },
 *  },
 * ]
 *
 * @param {string} imageUrl Contentful default image url
 * @param {Array} sizes Array of objects specifying sizes and variants.
 * @returns {Array} Array of objects. Each object specify the
 *  media query name and an array of urls.
 */
function getMultiScaledImages(imageUrl, sizes) {
  return sizes.map(function (val) {
    var variants = val.variants ? [].concat(toConsumableArray(val.variants)) : [];
    var url = val.url ? val.url : imageUrl;

    if (!variants.length) {
      variants = [];

      if (variantConfig['dpr2'] != null) {
        variants.push('dpr2');
      }

      if (variantConfig['dpr3'] != null && val.mediaQuery === 'default') {
        variants.push('dpr3');
      }
    }

    variants = ['default'].concat(toConsumableArray(variants));

    variants = variants.reduce(function (acc, variant) {
      var density = variantConfig.default.density;
      var quality = variantConfig.default.quality;
      var variantName = variant;

      if (_isObject(variant)) {
        variantName = variant.mediaQuery;

        if (variant.url) {
          url = variant.url;
        }

        if (_has(variant, ['params', 'density'])) {
          density = variant.params.density;
        } else if (variantConfig.hasOwnProperty(variantName)) {
          density = variantConfig[variantName].density;
        }

        if (_has(variant, ['params', 'q'])) {
          quality = variant.params.q;
        } else if (variantConfig.hasOwnProperty(variantName)) {
          quality = variantConfig[variantName].quality;
        }
      } else if (_isString(variant) && variantConfig.hasOwnProperty(variant)) {
        density = variantConfig[variant].density;
        quality = variantConfig[variant].quality;
      }

      var params = _extends({}, val.params, { q: quality });
      var returnVariant = { variant: variantName, density: density };

      if (params.w) {
        params.w = getRealDimension(density, params.w);
        returnVariant.width = params.w;
      }
      if (params.h) {
        params.h = getRealDimension(density, params.h);
        returnVariant.height = params.h;
      }

      returnVariant.url = getScaledImageUrl(url, params);

      acc.push(returnVariant);
      return acc;
    }, []);

    return {
      mediaQuery: val.mediaQuery,
      format: val.params.fm || getImageFormat(imageUrl),
      urls: variants
    };
  });
}

/**
 * Generates an array of media queries and their respective image url.
 * If used in the browser it can detect webp support.
 * The input array takes the form of:
 * [
 *  {
 *    mediaQuery: 'default',
 *    params: { w: 600, h: 470, fit: 'fill' },
 *    variants: ['dpr2', 'dpr3'],
 *  },
 *  {
 *    mediaQuery: 'sm',
 *    params: { w: 800, h: 600 },
 *  },
 * ]
 *
 * @param {string} imageUrl Contentful default image url.
 * @param {Array} sizes Array of objects specifying sizes and variants.
 * @param {string} webSupport Specify either true, false or 'only' to keep only webp urls.
 * @returns {string} Raw css styles.
 */
function getImagesArray(imageUrl, sizes) {
  var webpSupport = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;

  if (webpSupport === 'only') {
    sizes = addWebpFormat(sizes, true);
  } else if (webpSupport) {
    sizes = addWebpFormat(sizes);
  }

  var sizeUrls = getMultiScaledImages(imageUrl, sizes);

  return sizeUrls.reduce(function (acc, size) {
    var sizeName = size.mediaQuery;

    var sizeObject = size.urls.reduce(function (acc2, _ref) {
      var variant = _ref.variant,
          url = _ref.url;

      var mq = getMediaQueryForAliasVariantCombo(sizeName, variant);
      var key = mq || 'default';

      acc2.push([key, url]);
      return acc2;
    }, []);

    acc = [].concat(toConsumableArray(acc), toConsumableArray(sizeObject));
    return acc;
  }, []).reverse();
}

/**
 * Generates raw css for specified image sizes and variants.
 * The input array takes the form of:
 * [
 *  {
 *    mediaQuery: 'default',
 *    params: { w: 600, h: 470, fit: 'fill' },
 *    variants: ['dpr2', 'dpr3'],
 *  },
 *  {
 *    mediaQuery: 'sm',
 *    params: { w: 800, h: 600 },
 *  },
 * ]
 *
 * @param {string} imageUrl Contentful default image url
 * @param {Array} sizes Array of objects specifying sizes and variants.
 * @returns {string} Raw css styles.
 */
function getBackgroundImageRawCss(imageUrl, sizes) {
  var sizeUrls = getMultiScaledImages(imageUrl, sizes);

  var sizeCssChunks = sizeUrls.map(function (size) {
    var sizeName = size.mediaQuery;

    var sizeCssPieces = size.urls.map(function (_ref2) {
      var variant = _ref2.variant,
          url = _ref2.url;

      var mq = getMediaQueryForAliasVariantCombo(sizeName, variant);

      return mq ? '\n        @media ' + mq + ' {\n          background-image: url(' + url + ');\n        }\n      ' : '\n        background-image: url(' + url + ');\n      ';
    });

    return sizeCssPieces.join('');
  });

  return sizeCssChunks.join('');
}

/**
 * Generates a css class for specified image sizes and variants.
 * The input array takes the form of:
 * [
 *  {
 *    mediaQuery: 'default',
 *    params: { w: 600, h: 470, fit: 'fill' },
 *    variants: ['dpr2', 'dpr3'],
 *  },
 *  {
 *    mediaQuery: 'sm',
 *    params: { w: 800, h: 600 },
 *  },
 * ]
 *
 * @param {string} imageUrl Contentful default image url
 * @param {Array} sizes Array of objects specifying sizes and variants.
 * @returns {string} Css class to set on an element to apply the styles.
 */
function getBackgroundImageCss(imageUrl, sizes) {
  var rawCss = getBackgroundImageRawCss(imageUrl, sizes);
  return (/*#__PURE__*/css(rawCss)
  );
}

/**
 * Joins an object of HTML attributes together into a string.
 *
 * @param {Object} attrs HTML attributes keyed by attribute name.
 * @returns {string} HTML attribute string
 */
function buildAttributeString(attrs) {
  return _reduce(attrs, function (acc, val, key) {
    return val ? acc + ' ' + key + '="' + val + '"' : acc;
  }, '');
}

/**
 * Builds a <source> element for the ReactContentfulImage component
 *
 * @param {Object} img Image object from the Array returned by getMultiScaledImages()
 * @returns {Object} React element
 */
var buildSourceElement = function buildSourceElement(img) {
  var returnType = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'react';

  var type = img.format === 'jpg' ? 'image/jpeg' : 'image/' + img.format;
  var sizeMq = getMediaQueryForAlias(img.mediaQuery);

  var srcset = [].concat(toConsumableArray(img.urls)).reduce(function (acc, urlObj) {
    return urlObj.variant === 'default' ? '' + acc + urlObj.url + ',' : acc + '\n      ' + urlObj.url + ' ' + urlObj.density + 'x,';
  }, '');

  // Remove last comma
  srcset = srcset.substring(0, srcset.length - 1);

  if (returnType === 'string') {
    var attrs = {
      type: type,
      media: sizeMq,
      srcSet: srcset
    };

    return '<source ' + buildAttributeString(attrs) + ' />';
  }

  return React.createElement('source', {
    key: img.mediaQuery + '-' + img.format,
    type: type,
    media: sizeMq,
    srcSet: srcset
  });
};

/**
 * Adds webp to the formated of a media query sizes array.
 *
 * @param {Array} sizes Media Query Sizes array. See getMultiScaledImages
 *  for structure.
 * @param {Boolean} replace Webp format will replace the existing format rather than keeping both
 * @returns {Array} Media Query Sizes array with webp formatted added.
 */
function addWebpFormat(sizes) {
  var replace = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;

  return sizes.reduce(function (acc, size) {
    if (!replace) {
      acc.push(size);
    }

    acc.push(_merge({}, size, { params: { fm: 'webp' } }));
    return acc;
  }, []);
}

/**
 * Gets the url of the most appropriate fallback image from a sources array.
 * Assumes sizes are in order of largest to smallest mediaquery, and uses
 * the largest non-retina image size as the fallback.
 *
 * @param {Array} sources Sources array built via getMultiScaledImages().
 * @returns {string} URL of fallback image.
 */
function getFallbackImageFromSources(sources) {
  for (var i = 0; i < sources.length; i++) {
    var src = sources[i];

    if (src.format !== 'webp') {
      return src.urls.find(function (urlObj) {
        return urlObj.variant === 'default';
      }).url;
    }
  }
}

/**
 * Renders a static HTML markup string for given attributes and sizes.
 * Output should be identical to ReactContentfulImage React component if
 * it were rendered to an HTML string.
 *
 * @param {Object} attrs Attributes to be added to the img element.
 * @param {Array} sizes Media Query sizes array. See getMultiScaledImages
 *  for structure.
 * @returns {string} HTML markup string of either <img> or <picture>.
 */
function getStaticPictureMarkup(attrs) {
  var sizes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

  if (!attrs.src) {
    throw new Error('No image url was provided. Received: ' + attrs.src);
  }

  // Add Webp to formats and reverse to put fallback image last
  var sizesWithWebp = addWebpFormat(sizes).reverse();

  // Get Image urls for sizes
  var sources = getMultiScaledImages(attrs.src, sizesWithWebp);

  // Get fallback image url
  var fallback = getFallbackImageFromSources(sources) || attrs.src;

  var newAttrs = _extends({}, attrs, {
    src: fallback,
    class: attrs.class ? attrs.class + ' react-contentful-image' : 'react-contentful-image'
  });

  if (!sources.length) {
    return '<img ' + buildAttributeString(newAttrs) + ' />';
  }

  var sourceElements = sources.map(function (imgObj) {
    return buildSourceElement(imgObj, 'string');
  });

  return '<picture>\n    ' + sourceElements.join('\n') + '\n    <img ' + buildAttributeString(newAttrs) + ' />\n  </picture>';
}

/**
 * Get image url from sizes array for the current screen size.
 * Uses window.matchMedia().
 *
 * @param {string} imageUrl Contentful default image url
 * @param {Array} sizes Media Query sizes array. See getMultiScaledImages
 *  for structure.
 * @param {Boolean} webpOnly Forces webp format only.
 * @param {string} mediaQuery Custom mediaquery to check against
 * @returns {String} Url of the chosen image.
 */
function getImageForCurrentMatchingMediaQuery(imageUrl, sizes) {
  var mediaQuery = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;

  if (!canUseDOM() && !mediaQuery) {
    throw 'You must supply a media query or media query alias when using this function outside of a browser environment.';
  }

  if (mediaQuery) mediaQuery = getMediaQueryForAlias(mediaQuery);

  var imagesArray = canUseDOM() && canUseWebP() ? getImagesArray(imageUrl, sizes, 'only') : getImagesArray(imageUrl, sizes);

  var matchedUrl = imagesArray[imagesArray.length - 1].url;

  for (var i = 0; i < imagesArray.length; i++) {
    var _imagesArray$i = slicedToArray(imagesArray[i], 2),
        mq = _imagesArray$i[0],
        url = _imagesArray$i[1];

    if (canUseDOM && window.matchMedia(mq).matches) {
      matchedUrl = url;
      break;
    } else if (mediaQuery && mediaQuery === mq) {
      matchedUrl = url;
      break;
    }
  }

  return matchedUrl;
}

/**
 * Generates a react element that displays different images for various media queries.
 *
 * @param {Object} props Normal img element props along with a sizes prop specifying
 *  the image sizes to generate. See getMultiScaledImages() description for details.
 * @returns {Object} React element, either <picture> or <img>
 */
var ReactContentfulImage = function ReactContentfulImage(_ref3) {
  var _ref3$sizes = _ref3.sizes,
      sizes = _ref3$sizes === undefined ? [] : _ref3$sizes,
      src = _ref3.src,
      props = objectWithoutProperties(_ref3, ['sizes', 'src']);

  if (!src) {
    return React.createElement('img', props);
  }

  // Add Webp to formats and reverse to put fallback image last
  var sizesWithWebp = addWebpFormat(sizes).reverse();

  // Get Image urls for sizes
  var sources = getMultiScaledImages(src, sizesWithWebp);

  // Get Fallback image
  var fallback = getFallbackImageFromSources(sources) || src;

  return sources.length ? React.createElement(
    'picture',
    null,
    sources.map(buildSourceElement),
    React.createElement('img', _extends({ 'data-component': 'ReactContentfulImage', src: fallback }, props))
  ) : React.createElement('img', _extends({ src: fallback }, props));
};

ReactContentfulImage.propTypes = {
  src: PropTypes.string.isRequired,
  sizes: PropTypes.array
};

/* Alternative buildSourceElement: (This, type = 'react') method multiple source elements rather than srcset */
// const buildSourceElement = (img, type = 'react') => {
//   const type = img.format === 'jpg' ? 'image/jpeg' : `image/${img.format}`;

//   let srcset = [...img.urls].reverse().map(urlObj => {
//     const mq = getMediaQueryForAliasVariantCombo(img.size, urlObj.variant);
//     return <source type={type} media={mq} srcSet={urlObj.url} />;
//   });

//   return srcset;
// };

export { setup, getRealDimension, getImageFormat, getScaledImageUrl, getMediaQueryForAlias, getMediaQueryForAliasVariantCombo, getMultiScaledImages, getImagesArray, getBackgroundImageRawCss, getBackgroundImageCss, getStaticPictureMarkup, getImageForCurrentMatchingMediaQuery };
export default ReactContentfulImage;
